import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import { useAuth } from '../../auth/useAuth';
import { getPostLoginPath } from '../../services/billingApi';

const ProviderLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(username, password);
      sessionStorage.setItem('providerUsername', username);
      const returnTo = searchParams.get('next');
      const nextPath = await getPostLoginPath();
      if (
        returnTo &&
        returnTo.startsWith('/provider/') &&
        nextPath === '/provider/'
      ) {
        navigate(returnTo);
      } else {
        navigate(nextPath);
      }
    } catch (err) {
      if (err.code === 'NotAuthorizedException') {
        setError('Incorrect email or password.');
      } else if (err.code === 'UserNotConfirmedException') {
        setError('Please verify your email before signing in.');
      } else if (err.code === 'UserNotFoundException') {
        setError('No account found with that email.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProviderPageLayout
      variant="split"
      splitImage="/images/gallery-espresso.png"
      splitEyebrow="ReTechSys Provider Portal"
      splitQuote="Every visit, every cup — accounted for. Welcome back to a calmer way to run your venue."
    >
      <div className="provider-card provider-card--auth">
        <p className="provider-eyebrow">Welcome back</p>
        <h1 className="provider-title">Sign in</h1>
        <p className="provider-subtitle">
          Access your provider dashboard, transactions, and billing.
        </p>

        {error && <div className="provider-alert provider-alert--error">{error}</div>}

        <form onSubmit={handleLogin}>
          <div className="provider-form-group">
            <label htmlFor="username" className="provider-label">
              Email
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="provider-input"
              placeholder="you@company.com"
              autoComplete="username"
            />
          </div>

          <div className="provider-form-group">
            <label htmlFor="password" className="provider-label">
              Password
            </label>
            <div className="provider-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="provider-input"
                placeholder="Your password"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="provider-input-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} className="provider-btn provider-btn--primary">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="provider-footer-links">
          <button type="button" className="provider-link" onClick={() => navigate('/provider/forgot')}>
            Forgot password?
          </button>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)' }}>
            New provider?{' '}
            <button type="button" className="provider-link" onClick={() => navigate('/provider/signup')}>
              Create an account
            </button>
          </p>
        </div>
      </div>
    </ProviderPageLayout>
  );
};

export default ProviderLogin;
